import { X, Boxes, Search as SearchIcon } from "lucide-react";
import { CardWithImageAndHeading } from "../components/CardWithImageAndHeading";
import { SearchComponent } from "../components/Search";
import { EmptyState } from "../components/EmptyState";
import { ChangeEvent, useState, useMemo, useCallback, useEffect, useRef } from "react";
import { PlatformConnection } from "../types/tokens";

interface IProps {
  onClose: () => void;
  onClick: ({ id, clientId, scopes, connectionGuide, secretId, useDefaultOauthCreds }: { id: number; clientId?: string; scopes?: string; connectionGuide?: string; secretId?: string; useDefaultOauthCreds?: boolean }) => void;
  title?: string;
  connectedPlatforms: PlatformConnection[];
}

function getPlatformTag(p: PlatformConnection): string {
  return p.tags?.[0] || '';
}

// Fuzzy match: checks if all characters in query appear in order within the target
function fuzzyMatch(target: string, query: string): boolean {
  const t = target.toLowerCase();
  const q = query.toLowerCase();
  let ti = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const idx = t.indexOf(q[qi], ti);
    if (idx === -1) return false;
    ti = idx + 1;
  }
  return true;
}

const GRID_COLS = 4;

export const ConnectionsListSection = ({
  onClose,
  onClick,
  title = "Integrations",
  connectedPlatforms,
}: IProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setFocusedIndex(-1);
  };

  // Derive categories from platform data
  const categories = useMemo(() => {
    const cats = new Set<string>();
    connectedPlatforms?.forEach((p) => {
      const cat = getPlatformTag(p);
      if (cat) cats.add(cat);
    });
    return ['All', ...Array.from(cats).sort()];
  }, [connectedPlatforms]);

  // Filter platforms based on search and category
  const filteredPlatforms = useMemo(() => {
    let platforms = connectedPlatforms || [];
    if (searchTerm) {
      platforms = platforms.filter((p) => fuzzyMatch(p.title || '', searchTerm));
    } else if (activeCategory !== 'All') {
      platforms = platforms.filter((p) => getPlatformTag(p) === activeCategory);
    }
    return platforms;
  }, [connectedPlatforms, searchTerm, activeCategory]);

  const isSearching = searchTerm.length > 0;

  const navigablePlatforms = useMemo(() => {
    return filteredPlatforms;
  }, [filteredPlatforms]);

  const handlePlatformClick = useCallback((platform: PlatformConnection) => {
    const id = (platform?.connectionDefId || platform?.connectionDefinitionId) ?? 0;
    onClick({
      id,
      clientId: platform.clientIdDisplay || platform?.secret?.clientId,
      secretId: platform?.secretId || platform?.secretsServiceId,
      connectionGuide: platform?.guide,
      useDefaultOauthCreds: platform?.useDefaultOauthCreds,
    });
  }, [onClick]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex < 0 || !contentRef.current) return;
    const focusedEl = contentRef.current.querySelector('[data-focused="true"]');
    if (focusedEl) {
      focusedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const total = navigablePlatforms.length;
    if (total === 0) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < total) {
        handlePlatformClick(navigablePlatforms[focusedIndex]);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (focusedIndex < 0) {
        setFocusedIndex(0);
        return;
      }
      if (isSearching) {
        setFocusedIndex((prev) => (prev + 1) % total);
      } else {
        setFocusedIndex((prev) => {
          const next = prev + GRID_COLS;
          return next < total ? next : prev;
        });
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isSearching) {
        setFocusedIndex((prev) => (prev - 1 + total) % total);
      } else {
        setFocusedIndex((prev) => {
          const next = prev - GRID_COLS;
          return next >= 0 ? next : prev;
        });
      }
      return;
    }

    if (e.key === 'ArrowRight' && !isSearching) {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next = prev + 1;
        return next < total ? next : prev;
      });
      return;
    }

    if (e.key === 'ArrowLeft' && !isSearching) {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const next = prev - 1;
        return next >= 0 ? next : prev;
      });
      return;
    }
  }, [navigablePlatforms, focusedIndex, isSearching, onClose, handlePlatformClick]);

  if (connectedPlatforms?.length === 0) {
    return (
      <EmptyState
        message="No integrations available. Please activate integrations from the AuthKit dashboard."
        onClose={onClose}
        icon={Boxes}
      />
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 px-4 pt-4 pb-2">
        <div className="flex items-center justify-between w-full">
          <h1 className="text-[15px] font-semibold leading-tight">
            {title}
          </h1>
          <X
            size={13}
            className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors duration-150"
            style={{ width: '13px', height: '13px', minWidth: '13px', minHeight: '13px' }}
            onClick={onClose}
          />
        </div>

        <SearchComponent
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          platformCount={connectedPlatforms?.length}
        />

        {/* Category filter pills */}
        {!isSearching && categories.length > 2 && (
          <div className="category-pills flex gap-1.5 overflow-x-auto pb-0.5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setFocusedIndex(-1); }}
                className={`
                  whitespace-nowrap px-3 py-1 rounded-full text-[10px] font-medium
                  transition-all duration-150 shrink-0
                  ${activeCategory === cat
                    ? 'bg-foreground text-background'
                    : 'bg-card text-muted-foreground hover:bg-card-hover hover:text-foreground'
                  }
                `}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content area */}
      <div ref={contentRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 pt-1 pb-2">
        {filteredPlatforms.length === 0 ? (
          <div className="flex-1 w-full flex flex-col items-center justify-center gap-3 py-16">
            <SearchIcon
              size={48}
              className="opacity-15 text-muted-foreground"
            />
            <p className="text-[12px] text-center px-8 text-muted-foreground">
              No integrations found matching your search.
            </p>
          </div>
        ) : isSearching ? (
          /* Search results: compact list layout */
          <div key="list" className="flex flex-col gap-1 animate-fade-in">
            {filteredPlatforms.map((platform, i) => (
              <div
                key={platform?.connectionDefId || platform?.connectionDefinitionId}
                data-focused={focusedIndex === i}
                className={`flex items-center gap-3.5 px-3 py-2.5 rounded-[10px] cursor-pointer transition-all duration-150 group ${
                  focusedIndex === i
                    ? 'bg-card-hover'
                    : 'hover:bg-card-hover'
                }`}
                onClick={() => handlePlatformClick(platform)}
                onMouseEnter={() => setFocusedIndex(i)}
              >
                <img
                  src={platform?.image}
                  alt={platform?.title}
                  className="w-8 h-8 rounded-[8px] object-contain shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] font-medium text-foreground group-hover:text-foreground transition-colors duration-150">
                    {platform?.title}
                  </span>
                  <span className="ml-2 text-[10px] font-mono text-muted-foreground uppercase tracking-wide">
                    {getPlatformTag(platform)}
                  </span>
                </div>
                {platform?.connected ? (
                  <span className="text-[10px] font-mono text-emerald-400 shrink-0">
                    connected
                  </span>
                ) : (
                  <button className="text-[11px] font-medium px-3 py-1 rounded-full bg-card text-muted-foreground hover:bg-card-hover hover:text-foreground transition-all duration-150 shrink-0 opacity-0 group-hover:opacity-100">
                    Connect
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Browse mode: grid layout */
          <div key="grid" className="grid grid-cols-4 gap-2 animate-fade-in">
            {filteredPlatforms.map((platform, i) => (
              <div
                key={platform?.connectionDefId || platform?.connectionDefinitionId}
                data-focused={focusedIndex === i}
                onMouseEnter={() => setFocusedIndex(i)}
              >
                <CardWithImageAndHeading
                  id={(platform?.connectionDefId || platform?.connectionDefinitionId) ?? 0}
                  onClick={(id: number) =>
                    onClick({ id, clientId: platform.clientIdDisplay || platform?.secret?.clientId, secretId: platform?.secretId || platform?.secretsServiceId, connectionGuide: platform?.guide, useDefaultOauthCreds: platform?.useDefaultOauthCreds })
                  }
                  image={platform?.image}
                  heading={platform?.title}
                  category={getPlatformTag(platform)}
                  connected={platform?.connected}
                  focused={focusedIndex === i}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer keyboard hints */}
      <div className="flex items-center justify-center gap-3 px-4 py-2.5 border-t border-border">
        <span className="text-[10px] font-mono text-muted-foreground">
          <kbd className="text-muted-foreground">↑↓</kbd> navigate
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">·</span>
        <span className="text-[10px] font-mono text-muted-foreground">
          <kbd className="text-muted-foreground">↵</kbd> connect
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">·</span>
        <span className="text-[10px] font-mono text-muted-foreground">
          <kbd className="text-muted-foreground">esc</kbd> close
        </span>
      </div>
    </div>
  );
};
